/* DrumMidiMap — which MIDI note plays which drum of /virtual-drums, and which
 * note a drum is written as in an exported MIDI file. One table, both ways. The
 * same table plays the drum track of a MIDI file (GmDrumKit: /midi,
 * /virtual-orchestra) through gm(), and carries the level trims of the recorded
 * samples both use (SAMPLE_TRIM).
 *
 *   DrumMidiMap.gm(note)                     → piece for a General MIDI FILE's drum track
 *
 *   DrumMidiMap.resolve(note, set, learned)  → piece id | null   (set: 'kit' | 'perc')
 *   DrumMidiMap.exportNote(piece)            → the note a take is written with
 *   DrumMidiMap.notesOf(piece, set, learned) → every note that plays it now (for the map table)
 *   DrumMidiMap.learn(learned, set, note, piece) → new `learned` (a pad assigned by hitting it)
 *   DrumMidiMap.hatByPedal(piece, cc4, chooses) → an open-hat hit becomes closed while the pedal is
 *                                              down, unless the module picks open/closed itself
 *   DrumMidiMap.isCymbal(piece)              → a grab (poly aftertouch) chokes it
 *
 * Each list's FIRST note is the one written to a file; the rest are what real
 * modules send for the same drum (edge and rim zones, second crash, fourth tom).
 *
 * Why its own module (2026-10-30): the map lived as `gm:` on each piece plus a
 * line of aliases, and it was wrong for every real kit. Roland, Alesis and Yamaha
 * modules send their three toms as 48 / 45 / 43; the page sent 48 AND 45 to the
 * MIDDLE tom, so the high tom was unreachable from a drum kit, and its own export
 * wrote the high tom as 50. Percussion gave one note to two pieces three times
 * (61 bongo/darbuka, 62 conga/darbuka, 64 cajon/conga): the later piece won and
 * low bongo, high conga and cajon could not be played from MIDI at all.
 * Guard: tests/js/drum-midi-map.test.mjs. */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root && root.document) root.DrumMidiMap = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
    'use strict';

    /* Checked against the makers' default note tables (2026-10-30): Roland TD-07 /
       TD-17 / TD-27 / V71, Alesis Nitro / Nitro Max / Command / Nitro Ultimate XL,
       Yamaha DTX402/432/452. All three send toms as 48 / 45-or-47 / 43; where a
       note means something else in General MIDI (50 High Tom, 39 Hand Clap, 58
       Vibraslap, 55 Splash, 52 China, 59 Ride 2), every reading lands on the same
       drum or the e-kit reading is the common one. */
    var KIT = {
        kick:      [36, 35],
        snare:     [38, 40, 37],          /* head · rim shot / electric snare · cross-stick */
        hatClosed: [42, 22],              /* bow · edge (Roland TD, Alesis Nitro Ultimate) */
        hatOpen:   [46, 26, 23],          /* bow · edge · half-open (Alesis Nitro) */
        hatPedal:  [44, 21, 83],          /* foot close · foot splash (Alesis 21, Yamaha DTX 83) */
        tom3:      [48, 50],              /* high tom: T1 head 48 · T1 rim 50 = GM High Tom */
        tom1:      [45, 47],              /* mid tom:  T2 head 45 · T2 rim 47 · Yamaha DTX402 T2 head 47 */
        tom2:      [43, 58, 41, 39],      /* floor:    T3 head 43 · T3 rim 58 · T4 head 41 · T4 rim 39 */
        crash:     [49, 55, 57, 52],      /* crash 1 bow · edge · crash 2 bow · edge */
        ride:      [51, 59, 53]           /* bow · edge · bell */
    };

    /* Percussion has no General MIDI cajon or darbuka; each piece still gets a note
       of its own, the nearest in spirit, so nothing is unreachable and a file
       keeps the pieces apart. The aliases let a drum kit play the percussion set
       the way a drummer would reach for it: kick → cajon, snare → slap, toms →
       bongos and congas, hats → shaker, crashes → tambourine, ride → cowbell. */
    var PERC = {
        cajon:      [36, 35],
        cajonSlap:  [38, 40, 37],
        bongoHi:    [60, 48, 50],
        bongoLo:    [61, 45, 47],
        congaHi:    [62, 63, 43],
        congaLo:    [64, 58],
        darbuka:    [66],                 /* GM low timbale */
        darbukaTek: [65],                 /* GM high timbale */
        frame:      [41],                 /* GM low floor tom: a deep hand drum, not a clap */
        tambourine: [54, 46, 26, 49, 55, 57, 52],
        cowbell:    [56, 51, 59],
        claves:     [75],
        shaker:     [70, 69, 82, 42, 22, 44],
        agogoHi:    [67, 53],
        agogoLo:    [68],
        woodblock:  [76, 77]
    };

    /* A grab is polyphonic aftertouch on the cymbal's note: 127 when grabbed, 0
       when let go (Roland, Yamaha DTX). The hi-hat is NOT here: its pedal closes
       it (the pedal note is in the same choke group), and the Alesis Nitro
       Ultimate XL sends pedal MOVEMENT as aftertouch — that must not cut it. */
    var CYMBALS = { crash: 1, ride: 1 };

    function table(set) { return set === 'perc' ? PERC : KIT; }

    /* note → piece for a set, earlier positions winning: every piece's first note
       is claimed before any alias is, so an alias can never take another piece's
       own note. */
    var built = {};
    function defaults(set) {
        var key = set === 'perc' ? 'perc' : 'kit';
        if (built[key]) return built[key];
        var t = table(key), m = {}, depth = 0, more = true;
        while (more) {
            more = false;
            Object.keys(t).forEach(function (piece) {
                var n = t[piece][depth];
                if (n == null) return;
                more = true;
                if (m[n] == null) m[n] = piece;
            });
            depth++;
        }
        return (built[key] = m);
    }

    function own(learned, set) {
        var k = set === 'perc' ? 'perc' : 'kit';
        return (learned && learned[k] && typeof learned[k] === 'object') ? learned[k] : {};
    }

    function resolve(note, set, learned) {
        var o = own(learned, set);
        if (Object.prototype.hasOwnProperty.call(o, note)) return o[note] || null;
        var d = defaults(set);
        return d[note] != null ? d[note] : null;
    }

    function exportNote(piece) {
        if (KIT[piece]) return KIT[piece][0];
        if (PERC[piece]) return PERC[piece][0];
        return 38;
    }

    function notesOf(piece, set, learned) {
        var o = own(learned, set), d = defaults(set), out = [];
        Object.keys(d).forEach(function (n) {
            if (d[n] === piece && !Object.prototype.hasOwnProperty.call(o, n)) out.push(+n);
        });
        Object.keys(o).forEach(function (n) { if (o[n] === piece) out.push(+n); });
        var first = (table(set)[piece] || [])[0];
        out.sort(function (a, b) { return (a === first ? -1 : b === first ? 1 : a - b); });
        return out;
    }

    /* A pad hit while «Assign» is armed: that note now plays `piece`. A learned
       note that only repeats the default is dropped, so «reset» has nothing to
       forget and the table stays small. */
    function learn(learned, set, note, piece) {
        var k = set === 'perc' ? 'perc' : 'kit';
        var out = { kit: Object.assign({}, own(learned, 'kit')), perc: Object.assign({}, own(learned, 'perc')) };
        note = note | 0;
        if (note < 0 || note > 127) return out;
        if (defaults(k)[note] === piece) delete out[k][note];
        else out[k][note] = piece;
        return out;
    }

    /* CC 4 is the hi-hat pedal, rising from open to closed: 0–90 on Roland TD-02
       / TD-17, 0–127 on TD-27. Roland and Alesis modules choose 42 or 46 THEMSELVES
       from the pedal depth (TD-27 sends the closed note only at the very bottom,
       so a 46 at CC4 = 110 is a half-open hat the drummer meant). The pedal decides
       only for a device that never sends a closed-hat note of its own — a pad
       controller, or the Alesis Strike in «Note + CC#4» mode: `chooses` tells. */
    var PEDAL_CLOSED = 90;
    function hatByPedal(piece, cc4, chooses) {
        if (piece === 'hatOpen' && !chooses && cc4 != null && cc4 >= PEDAL_CLOSED) return 'hatClosed';
        return piece;
    }

    function isCymbal(piece) { return !!CYMBALS[piece]; }

    /* A MIDI FILE is General MIDI, a pad is a drum module, and they disagree on a
       few notes: 39 is a hand clap on a pop song's backbeat but the fourth tom's
       rim on a Roland or Alesis kit; 58 is a vibraslap, not a floor-tom rim. Files
       (/midi, /virtual-orchestra through GmDrumKit) read the kit table with these
       GM meanings on top, and GM hand percussion where the kit has no drum for it
       — the nearest piece that reads right in a mix, never silence. */
    var GM_FILE = {
        39: 'snare', 58: 'shaker',
        54: 'tambourine', 56: 'cowbell',
        60: 'bongoHi', 61: 'bongoLo', 62: 'congaHi', 63: 'congaHi', 64: 'congaLo',
        65: 'tom3', 66: 'tom1',                               /* timbales: the high and middle toms */
        67: 'agogoHi', 68: 'agogoLo',
        69: 'shaker', 70: 'shaker', 71: 'shaker', 72: 'shaker', 73: 'shaker', 74: 'shaker',
        75: 'claves', 76: 'woodblock', 77: 'woodblock',
        78: 'darbukaTek', 79: 'darbuka', 80: 'claves', 81: 'claves',
        82: 'shaker', 83: 'tambourine'                         /* GM2: shaker, jingle bell */
    };
    function gm(note) {
        if (GM_FILE[note]) return GM_FILE[note];
        return defaults('kit')[note] || null;
    }

    /* Level trims of the RECORDED samples (VCSL acoustic pack), measured as BS.1770
       momentary loudness at the default velocity, 30.10.2026: against the snare the
       ride came out at −18.8 dB — ten under the closed hat, where a drummer hears it
       level with the hat or above — and the pedal «chick» at −25 dB, real for the
       seat of an acoustic kit and inaudible on a phone or laptop speaker (users hit
       it again and again: silent_click). Trimmed toward the closed-hat level (−9) and
       −17 dB, checked on the page's own exported WAV (stereo, panned, vs the snare):
       ride −11.0 → −8.6 with the closed hat at −9.4…−11.5 (round-robin), pedal −19.5
       → −15.4. The synth kits (808/909, the fallback) are
       designed separately: the trim rides on the decoded sample only. */
    var SAMPLE_TRIM = { ride: 3.7, hatPedal: 3.4 };

    return {
        KIT: KIT,
        PERC: PERC,
        GM_FILE: GM_FILE,
        SAMPLE_TRIM: SAMPLE_TRIM,
        PEDAL_CLOSED: PEDAL_CLOSED,
        resolve: resolve,
        gm: gm,
        exportNote: exportNote,
        notesOf: notesOf,
        learn: learn,
        hatByPedal: hatByPedal,
        isCymbal: isCymbal,
        defaults: defaults
    };
});
